import React, { useMemo, useState } from 'react';
import {
  Col, Dropdown, Form, Row,
} from 'react-bootstrap';
import { useAchievementsContext } from '../../../../../../contexts/AchievementsContext/AchievementsContext';
import { MemoryBankCheckValue } from '../MemorySlotCompareForm';
import { CheckOperator, PlayerHasMatchType, PlayerHasMatchTypeLabels } from '../../../../Book/MemoryBank/memoryBankTypes';
import { AchievementRewardType } from '../../../../../../dorian-shared/types/achievement/Achievement';

interface RewardOption {
  value: number;
  label: string;
}

/**
 * Reward types that should be excluded from PlayerHas checks.
 * These are typically bonus episodes or other rewards managed through the achievements table.
 */
const EXCLUDED_REWARD_TYPES = new Set<AchievementRewardType>([
  AchievementRewardType.Episode, // Bonus episode rewards - managed separately
]);

/**
 * Type guard that validates a reward for PlayerHas dropdown inclusion
 * 
 * Checks that the reward:
 * - Has an ID (required for selection)
 * - Has a name (required for display)
 * - Has a valid reward type that's allowed for PlayerHas checks
 * 
 * @param reward - The reward to validate
 * @returns true if the reward is valid, with type narrowing
 */
const isRewardValidForPlayerHasDropdown = (reward: any): reward is { id: number; name: string; type: AchievementRewardType } => {
  // Must have ID and name
  if (!reward.id || !reward.name) {
    return false;
  }
  
  // Must have a defined reward type
  if (reward.type === undefined) {
    return false;
  }
  
  // Must not be an excluded reward type
  return !EXCLUDED_REWARD_TYPES.has(reward.type);
};


/**
 * Custom toggle button for the dropdown
 */
const RewardDropdownToggle = React.forwardRef<HTMLButtonElement, {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}>(({ children, onClick, disabled }, ref) => (
  <button
    type="button"
    ref={ref}
    disabled={disabled}
    className="form-control form-control-sm text-left border"
    style={{ background: 'white', cursor: disabled ? 'not-allowed' : 'pointer' }}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
    <span style={{ float: 'right' }}>▼</span>
  </button>
));

/**
 * Custom menu with filter input at top (like CustomSelect)
 */
const RewardDropdownMenu = React.forwardRef<HTMLDivElement, {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  filterValue: string;
  onFilterChange: (value: string) => void;
}>(({
  children, style, className, filterValue, onFilterChange,
}, ref) => (
  <div
    ref={ref}
    style={{ ...style, maxHeight: '300px', overflowY: 'auto', width: '100%' }}
    className={className}
  >
    <div className="mx-3 my-2">
      <Form.Control
        autoFocus
        size="sm"
        placeholder="Type to filter..."
        value={filterValue}
        onChange={(e) => onFilterChange(e.target.value)}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      />
    </div>
    <ul className="list-unstyled mb-0">
      {children}
    </ul>
  </div>
));

/**
 * Props for the PlayerHasAchievementForm component
 */
interface PlayerHasAchievementFormProps {
  /** Prefix for form input names */
  inputNamePrefix: string;
  /** Currently selected reward IDs (array) */
  checkInputValue: number[];
  /** Currently selected operator */
  operatorInputValue: string;
  /** Currently selected match type */
  matchInputValue?: PlayerHasMatchType;
  /** Step index in the branch */
  stepIndex: number;
  /** Callback when the selected reward changes */
  onChangeValue: (values: MemoryBankCheckValue[], stepIndex: number) => void;
  /** Whether the form is disabled */
  isDisabled?: boolean;
}

/**
 * Form component for selecting rewards in a playerHas check step
 *
 * This component allows creators to select multiple rewards to check if the player has earned them.
 * The dropdown shows all rewards from all achievements in the book.
 */
export function PlayerHasAchievementForm(props: PlayerHasAchievementFormProps) {
  const {
    inputNamePrefix,
    checkInputValue,
    operatorInputValue,
    matchInputValue = PlayerHasMatchType.All,
    stepIndex,
    onChangeValue,
    isDisabled,
  } = props;

  const [filterValue, setFilterValue] = useState('');
  const { achievements } = useAchievementsContext();

  // Flatten all rewards from all achievements into options
  // Only include rewards that are valid for PlayerHas checks
  // Deduplicate by reward ID since rewards can be linked to multiple achievements
  const rewardOptions: RewardOption[] = useMemo(() => {
    const seenIds = new Set<number>();
    const options: RewardOption[] = [];
    achievements.forEach((achievement) => {
      achievement.rewards?.forEach((reward) => {
        if (isRewardValidForPlayerHasDropdown(reward) && !seenIds.has(reward.id)) {
          seenIds.add(reward.id);
          const label = reward.name.replace(/^Unlocked: /, '🔓 ');
          options.push({ value: reward.id, label });
        }
      });
    });
    return options;
  }, [achievements]);

  // Ensure checkInputValue is always an array
  const selectedRewardIds = useMemo(() => {
    if (Array.isArray(checkInputValue)) {
      return checkInputValue;
    }
    // Handle legacy single value - convert to array
    if (checkInputValue && typeof checkInputValue === 'number') {
      return [checkInputValue];
    }
    return [];
  }, [checkInputValue]);

  // Sort options: selected first, then alphabetically (ignoring emojis)
  const sortedAndFilteredOptions = useMemo(() => {
    const filtered = filterValue
      ? rewardOptions.filter((opt) => opt.label.toLowerCase().includes(filterValue.toLowerCase()))
      : rewardOptions;

    return [...filtered].sort((a, b) => {
      const aSelected = selectedRewardIds.includes(a.value);
      const bSelected = selectedRewardIds.includes(b.value);

      // Selected items first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Then alphabetically
      return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    });
  }, [rewardOptions, selectedRewardIds, filterValue]);

  /**
   * Toggle a reward selection
   */
  const handleToggleReward = (rewardId: number) => {
    const newSelectedIds = selectedRewardIds.includes(rewardId)
      ? selectedRewardIds.filter((id) => id !== rewardId)
      : [...selectedRewardIds, rewardId];

    onChangeValue([
      { name: 'value', value: newSelectedIds },
      { name: 'variableId', value: 0 },
    ], stepIndex);
  };

  /**
   * Handle match type change
   */
  const handleMatchChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChangeValue([
      { name: 'match', value: event.target.value },
    ], stepIndex);
  };

  const selectedCount = selectedRewardIds.length;
  const toggleLabel = selectedCount > 0
    ? `${selectedCount} reward${selectedCount !== 1 ? 's' : ''} selected`
    : 'Select rewards...';

  return (
    <Row className="align-items-center g-2">
      <Col xs="auto">
        <Form.Control
          disabled={isDisabled}
          size="sm"
          as="select"
          name={`${inputNamePrefix}[operator]`}
          value={operatorInputValue}
          onChange={(event) => onChangeValue([{
            name: 'operator',
            value: event.target.value,
          }], stepIndex)}
        >
          {Object.entries(CheckOperator).map(([operatorKey, operatorValue]) => {
            if (!operatorValue.isMultiSelect && !operatorValue.isConditionList && !operatorValue.isPlayerHas) {
              return null;
            }
            return (
              <option key={`option${operatorKey}`} value={operatorKey}>
                {operatorValue.title}
              </option>
            );
          })}
        </Form.Control>
      </Col>
      <Col xs="auto">
        <Form.Control
          disabled={isDisabled}
          size="sm"
          as="select"
          value={matchInputValue}
          onChange={handleMatchChange}
          style={{ width: 'auto', paddingRight: '2rem' }}
        >
          {Object.entries(PlayerHasMatchTypeLabels).map(([matchKey, matchLabel]) => (
            <option key={matchKey} value={matchKey}>
              {matchLabel}
            </option>
          ))}
        </Form.Control>
        <input type="hidden" name={`${inputNamePrefix}[match]`} value={matchInputValue} />
      </Col>
      <Col xs="auto" className="d-flex align-items-center">
        <Form.Label className="mb-0">
          Rewards:
        </Form.Label>
      </Col>
      <Col>
          <Dropdown style={{ position: 'relative', width: '100%' }}>
            <Dropdown.Toggle
              as={RewardDropdownToggle}
              disabled={isDisabled}
            >
              {toggleLabel}
            </Dropdown.Toggle>
            <Dropdown.Menu
              as={RewardDropdownMenu}
              filterValue={filterValue}
              onFilterChange={setFilterValue}
            >
              {sortedAndFilteredOptions.map((option) => {
                const isSelected = selectedRewardIds.includes(option.value);
                return (
                  <Dropdown.Item
                    key={option.value}
                    as="button"
                    onClick={() => handleToggleReward(option.value)}
                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{option.label}</span>
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
          <input type="hidden" name={`${inputNamePrefix}[value]`} value={JSON.stringify(selectedRewardIds)} />
          <input type="hidden" name={`${inputNamePrefix}[variableId]`} value="0" />
      </Col>
    </Row>
  );
}

